import { Button, Flex, Menu, Tooltip, VisuallyHidden } from '@mantine/core';
import React, { useCallback, useId, useMemo } from 'react';
import StyledMenu from '@/components/StyledMenu/StyledMenu.tsx';
import classes from '@/pages/Run/components/StepCardActions.module.css';
import { HiArrowPathRoundedSquare, HiChevronDown } from 'react-icons/hi2';
import AppIcon from '@/components/AppIcon.tsx';
import RerunStepModal, { RerunStepModalData } from '@/components/RerunStepModal.tsx';
import StyledButton from '@/components/StyledButton/StyledButton.tsx';
import { ActiveStatuses, ExecutionStatus } from '@/types/statuses.ts';
import { Run } from '@/types/runs.ts';
import { StepRun } from '@/types/stepExecutions.ts';
import { AutomationStep } from '@/types/automationSteps.ts';
import { RERUN_STEP_CUTOFF } from '@/utils/constants.ts';
import { useIsHistoricalScopePresentation } from '@/pages/Run/components/HistoricalScopePresentationContext.ts';

const HISTORICAL_RERUN_DISABLED_MESSAGE = 'Rerunning prior results is not supported yet.';

type props = {
  run: Run;
  stepRun: StepRun;
  step?: AutomationStep;
  rerunAllData?: RerunStepModalData;
  rerunFailedData: RerunStepModalData | null; // null means no rerun failed option
  buttonOrIcon?: 'button' | 'icon';
  showOnHover?: boolean;
  hovered?: boolean;
};

const RerunMenu = ({
  run,
  stepRun,
  step,
  rerunAllData,
  rerunFailedData,
  buttonOrIcon = 'button',
  showOnHover = false,
  hovered = false,
}: props) => {
  const isHistoricalScopePresentation = useIsHistoricalScopePresentation();
  const disabledMessageId = useId();
  const rerunDisabledMessage =
    getRerunDisabledMessage(run, isHistoricalScopePresentation) ??
    (stepRun.rerun_eligibility?.allowed === false
      ? (stepRun.rerun_eligibility.reason ?? 'This result cannot be rerun.')
      : undefined);
  const rerunDisabled = !!rerunDisabledMessage;

  const [rerunData, setRerunData] = React.useState<RerunStepModalData | undefined>(undefined);

  const openRerunModal = useCallback(
    (data?: RerunStepModalData) => {
      if (rerunDisabled) {
        return;
      }

      setRerunData(data);
    },
    [rerunDisabled],
  );

  const target = useMemo(() => {
    return (
      <span>
        {buttonOrIcon === 'button' ? (
          <Button
            size={'xs'}
            variant={'default'}
            classNames={{ root: classes.button }}
            disabled={rerunDisabled}
            aria-describedby={rerunDisabledMessage ? disabledMessageId : undefined}
            px={'xs'}
          >
            Rerun
            <AppIcon Icon={HiChevronDown} size={12} ml={'4px'} />
          </Button>
        ) : (
          <StyledButton
            size={'sm'}
            variant={'subtleIcon'}
            aria-label="Rerun step"
            disabled={rerunDisabled}
            aria-describedby={rerunDisabledMessage ? disabledMessageId : undefined}
            style={{
              height: '20px',
              width: '20px',
              padding: '3px',
              borderRadius: '50%',
            }}
          >
            <AppIcon Icon={HiArrowPathRoundedSquare} size={14} />
          </StyledButton>
        )}
      </span>
    );
  }, [buttonOrIcon, disabledMessageId, rerunDisabled, rerunDisabledMessage]);

  const runTooOld = useMemo(() => RERUN_STEP_CUTOFF >= new Date(run.created_at), [run.created_at]);

  if ((showOnHover && !hovered && !rerunData) || runTooOld) {
    return <></>;
  }

  return (
    <Flex onClick={(e) => e.stopPropagation()}>
      <Tooltip label={rerunDisabledMessage} disabled={!rerunDisabledMessage} openDelay={250}>
        <span aria-describedby={rerunDisabledMessage ? disabledMessageId : undefined}>
          {rerunFailedData !== null ? (
            <StyledMenu position="bottom-end" withinPortal={false}>
              <Menu.Target>{target}</Menu.Target>
              <Menu.Dropdown>
                {rerunAllData && (
                  <Menu.Item disabled={rerunDisabled} onClick={() => openRerunModal(rerunAllData)}>
                    Rerun All
                  </Menu.Item>
                )}
                <Menu.Item disabled={rerunDisabled} onClick={() => openRerunModal(rerunFailedData)}>
                  Rerun Failed Only
                </Menu.Item>
              </Menu.Dropdown>
            </StyledMenu>
          ) : (
            <Flex onClick={() => openRerunModal(rerunAllData)}>{target}</Flex>
          )}
        </span>
      </Tooltip>
      {rerunDisabledMessage && (
        <VisuallyHidden id={disabledMessageId}>{rerunDisabledMessage}</VisuallyHidden>
      )}
      {!rerunDisabled && (
        <RerunStepModal
          run={run}
          stepRun={stepRun}
          step={step}
          rerunData={rerunData}
          opened={!!rerunData}
          setOpened={() => setRerunData(undefined)}
        />
      )}
    </Flex>
  );
};

const getRerunDisabledMessage = (run: Run, isHistoricalScopePresentation: boolean): string | undefined => {
  if (isHistoricalScopePresentation) return HISTORICAL_RERUN_DISABLED_MESSAGE;
  if (run.status === ExecutionStatus.Cancelled) return 'Run is cancelled. Create a new run to re-execute';
  if (ActiveStatuses.includes(run.status)) {
    return 'Run is active. Cancel the current execution or wait for it to complete before re-executing';
  }
  return undefined;
};

export default RerunMenu;
